/**
 * API hooks for iHerb posts with real-time polling and cursor-based pagination
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuthContext } from '@lib/auth';
import type { IherbPost, IherbPostPage, GenerateRequest, PostUpdate } from '../types';
import { IN_PROGRESS_STATUSES } from '../types';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8787';
const POLL_INTERVAL_MS = 2000;
const LOAD_MORE_BATCH = 25;

// =============================================================================
// useIherbPosts — list + real-time polling + cursor pagination
// =============================================================================

export type SortOrder = 'created_at_asc' | 'created_at_desc';

interface UseIherbPostsOptions {
  sort: SortOrder;
  /** Total items to load on initial fetch / refresh (grows with loadMore) */
  initialLimit: number;
  /** Comma-separated statuses to exclude from results */
  exclude?: string;
}

interface UseIherbPostsReturn {
  posts: IherbPost[];
  isLoading: boolean;
  isLoadingMore: boolean;
  isGenerating: boolean;
  hasMore: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  loadMore: () => Promise<void>;
  startPolling: () => void;
}

function buildUrl(params: {
  sort: SortOrder;
  limit: number;
  cursor?: string | null;
  exclude?: string;
}): string {
  const q = new URLSearchParams({ sort: params.sort, limit: String(params.limit) });
  if (params.cursor) q.set('cursor', params.cursor);
  if (params.exclude) q.set('exclude', params.exclude);
  return `${API_BASE}/iherb-posts?${q}`;
}

export function useIherbPosts({
  sort,
  initialLimit,
  exclude,
}: UseIherbPostsOptions): UseIherbPostsReturn {
  const { getAccessToken } = useAuthContext();
  const [posts, setPosts] = useState<IherbPost[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [forcePoll, setForcePoll] = useState(false);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Track current loaded count for refetch (grows as user loads more)
  const loadedCountRef = useRef(initialLimit);

  const isGenerating = posts.some((p) => (IN_PROGRESS_STATUSES as string[]).includes(p.status));

  useEffect(() => {
    if (isGenerating && forcePoll) setForcePoll(false);
  }, [isGenerating, forcePoll]);

  // fetchFresh: load first `count` items with no cursor (used for initial load and refetch)
  const fetchFresh = useCallback(
    async (count: number) => {
      try {
        const token = await getAccessToken();
        const res = await fetch(buildUrl({ sort, limit: count, exclude }), {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error(`Failed to fetch posts: ${res.status}`);
        const page: IherbPostPage = await res.json();
        setPosts(page.items);
        setNextCursor(page.next_cursor);
        setHasMore(page.next_cursor !== null);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch posts');
      }
    },
    [getAccessToken, sort, exclude]
  );

  // Initial load — reset when sort / exclude / initialLimit change
  useEffect(() => {
    loadedCountRef.current = initialLimit;
    setIsLoading(true);
    fetchFresh(initialLimit).finally(() => setIsLoading(false));
  }, [fetchFresh, initialLimit]);

  // refetch: re-load however many items are currently shown
  const refetch = useCallback(async () => {
    await fetchFresh(loadedCountRef.current);
  }, [fetchFresh]);

  // loadMore: fetch next batch using cursor, append
  const loadMore = useCallback(async () => {
    if (!nextCursor || isLoadingMore) return;
    setIsLoadingMore(true);
    try {
      const token = await getAccessToken();
      const res = await fetch(
        buildUrl({ sort, limit: LOAD_MORE_BATCH, cursor: nextCursor, exclude }),
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) throw new Error(`Failed to load more: ${res.status}`);
      const page: IherbPostPage = await res.json();
      setPosts((prev) => [...prev, ...page.items]);
      setNextCursor(page.next_cursor);
      setHasMore(page.next_cursor !== null);
      loadedCountRef.current += page.items.length;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load more');
    } finally {
      setIsLoadingMore(false);
    }
  }, [nextCursor, isLoadingMore, getAccessToken, sort, exclude]);

  // Poll during generation
  useEffect(() => {
    const shouldPoll = isGenerating || forcePoll;
    if (shouldPoll) {
      pollRef.current = setInterval(refetch, POLL_INTERVAL_MS);
    } else {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    }
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [isGenerating, forcePoll, refetch]);

  const startPolling = useCallback(() => setForcePoll(true), []);

  return {
    posts,
    isLoading,
    isLoadingMore,
    isGenerating,
    hasMore,
    error,
    refetch,
    loadMore,
    startPolling,
  };
}

// =============================================================================
// useGeneratePosts — trigger background generation
// =============================================================================

interface UseGeneratePostsReturn {
  generatePosts: (req: GenerateRequest) => Promise<void>;
  isStarting: boolean;
  error: string | null;
}

export function useGeneratePosts(): UseGeneratePostsReturn {
  const { getAccessToken } = useAuthContext();
  const [isStarting, setIsStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const generatePosts = useCallback(
    async (req: GenerateRequest) => {
      setIsStarting(true);
      setError(null);
      try {
        const token = await getAccessToken();
        const response = await fetch(`${API_BASE}/iherb-posts/generate`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(req),
        });
        if (!response.ok) {
          const data = await response.json().catch(() => ({}));
          throw new Error(data.detail || `Failed to start generation: ${response.status}`);
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to start generation';
        setError(message);
        throw err;
      } finally {
        setIsStarting(false);
      }
    },
    [getAccessToken]
  );

  return { generatePosts, isStarting, error };
}

// =============================================================================
// useUpdatePost — approve / reject
// =============================================================================

interface UseUpdatePostReturn {
  updatePost: (id: string, update: PostUpdate) => Promise<IherbPost>;
  isUpdating: boolean;
  error: string | null;
}

export function useUpdatePost(): UseUpdatePostReturn {
  const { getAccessToken } = useAuthContext();
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const updatePost = useCallback(
    async (id: string, update: PostUpdate): Promise<IherbPost> => {
      setIsUpdating(true);
      setError(null);
      try {
        const token = await getAccessToken();
        const response = await fetch(`${API_BASE}/iherb-posts/${id}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(update),
        });
        if (!response.ok) {
          const data = await response.json().catch(() => ({}));
          throw new Error(data.detail || `Failed to update post: ${response.status}`);
        }
        return (await response.json()) as IherbPost;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to update post';
        setError(message);
        throw err;
      } finally {
        setIsUpdating(false);
      }
    },
    [getAccessToken]
  );

  return { updatePost, isUpdating, error };
}

// =============================================================================
// useSendPost — dispatch approved post to webhook
// =============================================================================

interface UseSendPostReturn {
  sendPost: (id: string) => Promise<IherbPost>;
  isSending: boolean;
  error: string | null;
}

export function useSendPost(): UseSendPostReturn {
  const { getAccessToken } = useAuthContext();
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const sendPost = useCallback(
    async (id: string): Promise<IherbPost> => {
      setIsSending(true);
      setError(null);
      try {
        const token = await getAccessToken();
        const response = await fetch(`${API_BASE}/iherb-posts/${id}/send`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) {
          const data = await response.json().catch(() => ({}));
          throw new Error(data.detail || `Failed to send post: ${response.status}`);
        }
        return (await response.json()) as IherbPost;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to send post';
        setError(message);
        throw err;
      } finally {
        setIsSending(false);
      }
    },
    [getAccessToken]
  );

  return { sendPost, isSending, error };
}

// =============================================================================
// useRefinePost — re-run judge with post's current feedback
// =============================================================================

interface UseRefinePostReturn {
  refinePost: (id: string) => Promise<IherbPost>;
  isRefining: boolean;
}

export function useRefinePost(): UseRefinePostReturn {
  const { getAccessToken } = useAuthContext();
  const [isRefining, setIsRefining] = useState(false);

  const refinePost = useCallback(
    async (id: string): Promise<IherbPost> => {
      setIsRefining(true);
      try {
        const token = await getAccessToken();
        const response = await fetch(`${API_BASE}/iherb-posts/${id}/refine`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) {
          const data = await response.json().catch(() => ({}));
          throw new Error(data.detail || `Failed to refine post: ${response.status}`);
        }
        return (await response.json()) as IherbPost;
      } finally {
        setIsRefining(false);
      }
    },
    [getAccessToken]
  );

  return { refinePost, isRefining };
}

// =============================================================================
// useDeletePost
// =============================================================================

interface UseDeletePostReturn {
  deletePost: (id: string) => Promise<void>;
  isDeleting: boolean;
  error: string | null;
}

export function useDeletePost(): UseDeletePostReturn {
  const { getAccessToken } = useAuthContext();
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const deletePost = useCallback(
    async (id: string): Promise<void> => {
      setIsDeleting(true);
      setError(null);
      try {
        const token = await getAccessToken();
        const response = await fetch(`${API_BASE}/iherb-posts/${id}`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) {
          const data = await response.json().catch(() => ({}));
          throw new Error(data.detail || `Failed to delete post: ${response.status}`);
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to delete post';
        setError(message);
        throw err;
      } finally {
        setIsDeleting(false);
      }
    },
    [getAccessToken]
  );

  return { deletePost, isDeleting, error };
}
